/* app/pipeline-inbox.jsx - Inbox two-pane capture & triage, wired to /api/inbox
   (list/detail/create/fetch-url/summarize/patch/status/approve-share/delete).

   THE SWITCHER IS A FILTER HERE, which is step 5 of
   docs/PROPERTY-DB-MIGRATION.md and the client half of what routes/inbox.js
   already answers. Inbox is "property, filtered" (docs/MODULE-SCOPE.md 3), so
   the list is the WHOLE WEBSITE's captured items and the switcher position
   narrows it. Two pieces of state, and they are not the same thing: `site` is
   the anchor connection and rides on every request because it is what
   authorises it, and `sectionId` is the filter, null at the All position.

   AND INBOX AGGREGATES (MODULE-SCOPE 1.1), so this screen is allowed a total:
   a captured item is a captured item whichever section holds it. What a total
   is not allowed to be is unverifiable, so at All the per section breakdown is
   on screen beside it. */
import { api } from '../lib/api.js';
(function () {
  const { useState, useRef, useEffect, useCallback } = React;
  function I(props) { return React.createElement(window.MlIcon, props); }

  const hostOf = (u) => String(u || "").replace(/^https?:\/\//, "").replace(/^www\./, "").replace(/\/+$/, "");
  // Source URLs may arrive scheme-less; a bare href would resolve relative to
  // the app, so re-attach https:// for the outbound link.
  const outbound = (u) => {
    const s = String(u || "").trim();
    return s ? (/^https?:\/\//i.test(s) ? s : "https://" + s) : undefined;
  };
  function mapInbox(it) {
    const todos = Array.isArray(it.todos) ? it.todos.map(t => Array.isArray(t) ? t : [(t && (t.text || t.title)) || String(t), !!(t && t.done)]) : [];
    return {
      id: it.id, title: it.title || "(untitled)",
      pri: it.priority && it.priority !== "none" ? it.priority : "low",
      status: it.status === "in_review" ? "review" : it.status,
      source: it.source_url ? hostOf(it.source_url) : (it.source_type || "manual"),
      when: it.created_at ? String(it.created_at).slice(0, 10) : "",
      url: it.source_url || null, summary: it.ai_summary || null, content: it.raw_content || "",
      tags: Array.isArray(it.tags) ? it.tags : [], todos,
      linked: it.linked_post_id ? "Draft #" + it.linked_post_id : null, _raw: it,
      // The two facts the row now carries, kept apart because they answer
      // different questions. `section_name` is WHOSE INBOX HOLDS IT, which is
      // what this list has always answered and what the Section column shows.
      // `shared_from_name` is WHO SENT IT, which is provenance on the row: a
      // shared entity creates a second row on the receiving section, so the
      // sender is data about the item and never the section it lives in. The
      // ids they come with are not carried over, because nothing a row can be
      // asked to do here names a site: every per row route on this screen is
      // addressed by the item's own id. `_raw` keeps the whole row for the day
      // one of them is not.
      sectionName: it.section_name || null,
      canPublish: it.can_publish,
      sectionNotDerived: !!it.section_not_derived_yet,
      sharedFromName: it.shared_from_name || null,
    };
  }

  // ---- the two row labels ----
  // Visibly different on purpose. A reader who cannot tell "this item is in the
  // Blog's inbox" from "the Blog sent us this item" is reading one label for
  // two facts, and the list answers the first.
  function SectionChip({ item }) {
    if (!item.sectionName) return null;
    const why = (item.canPublish === false ? item.sectionName + " is read by crawling it and has no CMS behind it. " : "")
      + "This is the piece of the website whose inbox holds the item"
      + (item.sectionNotDerived ? ", named from the connection it arrived through because its section column has not been filled in yet." : ".");
    return (
      <span className="inbox-item__src" title={why}
            style={{ display: "inline-flex", alignItems: "center", gap: 4, fontWeight: 600, color: "var(--text-secondary)" }}>
        <I name="layers" size={11} />{item.sectionName}
      </span>
    );
  }
  function SharedChip({ item }) {
    if (!item.sharedFromName) return null;
    return (
      <span className="inbox-item__src" title={`Sent from ${item.sharedFromName}. The item itself lives in ${item.sectionName || "this"} inbox.`}
            style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "var(--primary)", fontStyle: "italic" }}>
        <I name="share" size={11} />from {item.sharedFromName}
      </span>
    );
  }

  // A bi-share item's raw_content is the JSON payload written by
  // POST /bi/entities/:id/share: { __bi_share, entity_type, name, url, data,
  // relationships_to_me, from_site_id, from_site_label }. The list endpoint
  // omits raw_content, so this runs on the detail fetch. The payload crossed
  // sites and plugin versions, so a malformed one must degrade to generic
  // labels, never take down the pane. The tags carry the entity type too,
  // which gives a usable kind/module even before (or without) the content.
  const SHARE_MOD = { competitor: "Competitors", partner: "Partners", product: "Products", industry: "Industry" };
  const SHARE_RELS = ["look", "comp", "alt"];
  function shareInfo(item, content) {
    const tagKind = (item.tags || []).find(t => SHARE_MOD[t]);
    const raw = item._raw || {};
    const info = {
      shareKind: tagKind || "entity",
      shareModule: SHARE_MOD[tagKind] || "BI",
      shareSite: raw.source_site_id ? "site " + raw.source_site_id : "another site",
      rels: [], shareUrl: null,
    };
    try {
      const p = JSON.parse(content || "");
      if (p && p.__bi_share) {
        if (SHARE_MOD[p.entity_type]) { info.shareKind = p.entity_type; info.shareModule = SHARE_MOD[p.entity_type]; }
        if (p.from_site_label) info.shareSite = String(p.from_site_label);
        if (Array.isArray(p.relationships_to_me)) info.rels = p.relationships_to_me.filter(r => SHARE_RELS.includes(r));
        if (p.url) info.shareUrl = String(p.url);
      }
    } catch { /* not JSON: keep the tag-derived fallbacks */ }
    return info;
  }

  const FILTERS = [["all", "All"], ["new", "New"], ["review", "In review"], ["used", "Used"], ["archived", "Archived"]];
  const FILTER_COLOR = { all: "var(--primary)", new: "var(--warning)", review: "var(--info)", used: "var(--success)", archived: "var(--text-muted)" };
  const statusPill = (s) => { const m = { new: ["inbox-st-new", "New"], review: ["inbox-st-review", "In review"], used: ["inbox-st-used", "Used"], archived: ["inbox-st-archived", "Archived"] }[s]; return <span className={"inbox-status-pill " + m[0]}><span className="dot" />{m[1]}</span>; };

  // ---- status filter dropdown (replaces the cramped tab strip) ----
  function StatusFilter({ value, counts, onPick }) {
    const [open, setOpen] = useState(false);
    const cur = FILTERS.find(f => f[0] === value);
    return (
      <div className="bi-filterpill bi-filterpill--active" style={{ flex: 1 }}>
        <button className="bi-filterpill__btn" style={{ width: "100%", justifyContent: "space-between" }} onClick={() => setOpen(o => !o)}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}><span className="bi-filterpill__dot" style={{ background: FILTER_COLOR[value] }} />{cur[1]}<span className="inbox-tab-count">{counts[value]}</span></span>
          <I name="chevron-down" size={14} />
        </button>
        {open && <>
          <div style={{ position: "fixed", inset: 0, zIndex: 29 }} onClick={() => setOpen(false)} />
          <div className="bi-filtermenu" style={{ left: 0, right: 0, minWidth: 0 }}>
            {FILTERS.map(([id, l]) => (
              <button key={id} className="bi-filtermenu__item" onClick={() => { onPick(id); setOpen(false); }}>
                <span className="bi-filterpill__dot" style={{ background: FILTER_COLOR[id] }} />{l}<span className="bi-filtermenu__n">{counts[id]}</span>{value === id && <span className="bi-filtermenu__check"><I name="check" size={15} /></span>}
              </button>
            ))}
          </div>
        </>}
      </div>
    );
  }

  // ---- the breakdown behind the counts ----
  // GET /inbox/stats answers the four counts over the switcher's position and
  // hands back the parts they are made of. The parts are on screen at All
  // because a total nobody can check is a total nobody should be shown, and
  // every piece of the website keeps its row even at zero: emptiness is a state
  // a module renders, never a reason to leave something out (MODULE-SCOPE 2).
  //
  // One line until it is asked for, because the item list is the screen and
  // this is the arithmetic behind its header, not a second subject.
  const STAT_COLS = [["new", "New"], ["in_review", "In review"], ["used", "Used"], ["archived", "Archived"]];
  const statSum = (o) => STAT_COLS.reduce((n, [k]) => n + (Number(o && o[k]) || 0), 0);
  function Breakdown({ stats, err, onRetry }) {
    const [open, setOpen] = useState(false);
    if (err) {
      return (
        <div className="bi-hint" style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
          <I name="alert-triangle" size={14} style={{ color: "var(--warning)" }} />
          The per piece breakdown of these items could not be read, so the list below is the only answer on screen: {err}
          <button className="bi-btn bi-btn--sm" onClick={onRetry}>Retry</button>
        </div>
      );
    }
    if (!stats) return null;
    const secs = Array.isArray(stats.sections) ? stats.sections : [];
    const un = stats.unattributed || null;
    const unTotal = statSum(un);
    const total = statSum(stats);
    const cell = { padding: "5px 10px", fontSize: 12, textAlign: "right", color: "var(--text-secondary)", whiteSpace: "nowrap" };
    const head = { ...cell, fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "var(--tracking-wide)" };
    const nameCell = { ...cell, textAlign: "left", color: "var(--text-primary)" };
    return (
      <div className="bi-card" style={{ marginBottom: 12, padding: 0, overflow: "hidden" }}>
        <button className="bi-filterpill__btn" style={{ width: "100%", justifyContent: "space-between", padding: "10px 12px" }} onClick={() => setOpen(o => !o)}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: "var(--text-sm)" }}>
            <I name="layers" size={14} />
            {/* "0 pieces of this website" is not an answer, and the case it
                would describe is a connection that is not grouped under a
                property yet, which is a different state from an empty one. The
                reason the server gives is in the drawer below. */}
            <b>{total}</b> captured {total === 1 ? "item" : "items"}
            {secs.length ? ` across ${secs.length} ${secs.length === 1 ? "piece" : "pieces"} of this website`
              : ", held by this connection alone"}
            <span style={{ color: "var(--text-muted)" }}>
              {STAT_COLS.map(([k, l]) => `${l} ${Number(stats[k]) || 0}`).join(" · ")}
            </span>
          </span>
          <I name={open ? "chevron-down" : "chevron-right"} size={14} />
        </button>
        {open && (
          <div style={{ borderTop: "1px solid var(--border-subtle)", padding: "4px 2px 10px", overflowX: "auto" }}>
            {secs.length > 0 && <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ ...head, textAlign: "left" }}>Piece of the website</th>
                  {STAT_COLS.map(([k, l]) => <th key={k} style={head}>{l}</th>)}
                  <th style={head}>Total</th>
                </tr>
              </thead>
              <tbody>
                {secs.map(s => (
                  <tr key={s.section_id} style={{ borderTop: "1px solid var(--border-subtle)" }}>
                    <td style={nameCell}>
                      {s.name}
                      {/* The per row verb's one fact, named rather than left to
                          be guessed from a blank: a piece read by crawling has
                          no CMS, whatever is in its inbox. */}
                      {s.can_publish === false && <span style={{ color: "var(--text-muted)", fontWeight: 400 }}> · crawled, no CMS</span>}
                    </td>
                    {STAT_COLS.map(([k]) => <td key={k} style={cell}>{Number(s[k]) || 0}</td>)}
                    <td style={{ ...cell, fontWeight: 600, color: "var(--text-primary)" }}>{Number(s.total) || 0}</td>
                  </tr>
                ))}
                {/* Rows held by a connection this website does not list are
                    counted apart rather than dropped, so the parts still add to
                    the whole. Hidden only when there are none, because a zero
                    here is the normal case and a line saying so would be noise
                    on every load. */}
                {unTotal > 0 && (
                  <tr style={{ borderTop: "1px solid var(--border-subtle)" }}>
                    <td style={nameCell} title="Held by a connection that is not one of this website's pieces, so it is counted here rather than dropped.">Not attributed to a piece</td>
                    {STAT_COLS.map(([k]) => <td key={k} style={cell}>{Number(un[k]) || 0}</td>)}
                    <td style={{ ...cell, fontWeight: 600, color: "var(--text-primary)" }}>{unTotal}</td>
                  </tr>
                )}
              </tbody>
            </table>}
            {secs.length > 0 && stats.sections_why && <div className="bi-hint" style={{ padding: "8px 10px 0" }}>{stats.sections_why}</div>}
            {/* Which filter answered, when it was not the property's own
                column: a count reading zero because an inbox is empty and one
                reading zero because the read fell back are the same number and
                not the same fact. */}
            {stats.read_filter && stats.read_filter !== "property_id" && stats.read_filter_why
              && <div className="bi-hint" style={{ padding: "6px 10px 0" }}>{stats.read_filter_why}</div>}
          </div>
        )}
      </div>
    );
  }

  // ---- add content ----
  // The "Add content" button had no handler, so the Inbox could be read and
  // triaged but never filled: every item in it had arrived from a BI share.
  // Both halves of this already existed on the backend, POST /inbox/fetch-url
  // to read a page and POST /inbox to store the item.
  // `siteId` is the ANCHOR, which is where a captured item lands, and that is
  // the same connection the switcher is standing on whenever it names a
  // section. At All it is still one inbox out of several, so the dialog says
  // which one rather than leaving a person to find out after saving.
  function AddContent({ siteId, siteLabel, whole, onAdded, onClose }) {
    const [mode, setMode] = useState("paste");   // paste | url
    const [url, setUrl] = useState("");
    const [title, setTitle] = useState("");
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    // Reading the page fills the form rather than saving straight away: the
    // extraction can be wrong, and the user should see it before it is stored.
    const fetchUrl = async () => {
      const u = url.trim();
      if (!u) { setErr("Enter a URL to read."); return; }
      setBusy(true); setErr(null);
      try {
        const r = await api(`/inbox/fetch-url`, { method: "POST", body: JSON.stringify({ url: u }) });
        setTitle(r.title || u);
        setText(r.content || "");
        if (r.source_url) setUrl(r.source_url);
      } catch (e) { setErr((e.data && e.data.message) || e.message || "Could not read that page."); }
      finally { setBusy(false); }
    };

    const save = async () => {
      const t = title.trim() || (mode === "url" ? url.trim() : text.trim().slice(0, 80));
      if (!t) { setErr("Add a title, or some content to take one from."); return; }
      if (siteId == null) { setErr("No active site."); return; }
      setBusy(true); setErr(null);
      try {
        await api(`/inbox`, { method: "POST", body: JSON.stringify({
          site_id: siteId, title: t.slice(0, 500),
          source_type: mode === "url" ? "url" : "manual",
          source_url: mode === "url" ? (url.trim() || null) : null,
          raw_content: text || null, status: "new",
        }) });
        onAdded();
      } catch (e) { setErr(e.message || "Could not save it."); setBusy(false); }
    };

    return (
      <div className="bi-scrim" onMouseDown={ev => { if (ev.target === ev.currentTarget && !busy) onClose(); }}>
        <div className="bi-modal" role="dialog" aria-modal="true">
          <div className="bi-modal__head">
            <div><div className="bi-modal__title">Add content</div><div className="bi-modal__sub">Paste something, or read a page. Summarise it after it lands.{whole && siteLabel ? ` It goes into ${siteLabel}'s inbox.` : ""}</div></div>
            <button className="bi-iconbtn bi-modal__close" onClick={onClose} disabled={busy} aria-label="Close"><I name="x" size={18} /></button>
          </div>
          <div className="bi-modal__body">
            <div className="hds-tabs" style={{ marginBottom: 12 }}>
              <button className={"hds-tab " + (mode === "paste" ? "hds-tab--active" : "")} onClick={() => { setMode("paste"); setErr(null); }}>Paste text</button>
              <button className={"hds-tab " + (mode === "url" ? "hds-tab--active" : "")} onClick={() => { setMode("url"); setErr(null); }}>Fetch a URL</button>
            </div>
            {mode === "url" && (
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <div className="hds-input-group" style={{ flex: 1 }}>
                  <span className="hds-input-group__icon"><I name="link" size={16} /></span>
                  <input className="hds-input" value={url} placeholder="https://example.com/article" onChange={e => setUrl(e.target.value)} onKeyDown={e => { if (e.key === "Enter") fetchUrl(); }} />
                </div>
                <button className="bi-btn" disabled={busy || !url.trim()} onClick={fetchUrl}>{busy ? "Reading…" : "Read page"}</button>
              </div>
            )}
            <label className="bi-field__label">Title <span className="bi-field__req">*</span></label>
            <input className="hds-input" value={title} onChange={e => setTitle(e.target.value)} placeholder={mode === "url" ? "Filled in when the page is read" : "What is this?"} style={{ marginBottom: 12 }} />
            <label className="bi-field__label">Content</label>
            <textarea className="hds-textarea" rows={8} value={text} onChange={e => setText(e.target.value)} placeholder="Paste an article, notes or a transcript…" />
            {err && <div className="bi-hint" style={{ color: "var(--danger)" }}>{err}</div>}
          </div>
          <div className="bi-modal__foot">
            <button className="bi-btn" onClick={onClose} disabled={busy}>Cancel</button>
            <button className="bi-btn bi-btn--primary" disabled={busy} onClick={save}>{busy ? "Saving…" : "Add to inbox"}</button>
          </div>
        </div>
      </div>
    );
  }

  // ---- detail panel ----
  function Detail({ item, contentState, onRetryContent, onToast, onGenerate, onBack, onSummarise, onArchive, onDelete, onPatch, onApproveShare }) {
    const [editing, setEditing] = useState(false);
    const [title, setTitle] = useState(item.title);
    const [tags, setTags] = useState(item.tags || []);
    const [todos, setTodos] = useState(item.todos || []);
    const [expanded, setExpanded] = useState(false);
    const [summarising, setSummarising] = useState(false);
    const [summary, setSummary] = useState(item.summary);
    const [delStep, setDelStep] = useState(0);
    const [archived, setArchived] = useState(item.status === "archived");
    const [tagInput, setTagInput] = useState("");
    const [todoInput, setTodoInput] = useState("");
    const [sharing, setSharing] = useState(false);
    useEffect(() => { setTitle(item.title); setTags(item.tags || []); setTodos(item.todos || []); setSummary(item.summary); setEditing(false); setExpanded(false); setDelStep(0); setArchived(item.status === "archived"); }, [item.id]);

    // Title, tags and todos were local state only: typed in, never sent, and
    // wiped by the effect above the moment another item was selected. Each edit
    // now persists through PATCH /inbox/:id, which already accepted all three.
    const persist = async (patch, optimistic) => {
      optimistic && optimistic();
      try { await onPatch(item.id, patch); }
      catch (e) { onToast && onToast(e.message || "Could not save that change"); }
    };
    const commitTitle = () => {
      setEditing(false);
      const t = title.trim();
      if (!t || t === item.title) { setTitle(item.title); return; }
      persist({ title: t });
    };
    const addTag = (t) => { const next = [...tags, t]; persist({ tags: next }, () => setTags(next)); };
    const removeTag = (t) => { const next = tags.filter(x => x !== t); persist({ tags: next }, () => setTags(next)); };
    const addTodo = (t) => { const next = [...todos, [t, false]]; persist({ todos: next }, () => setTodos(next)); };
    const toggleTodo = (i) => { const next = todos.map((x, j) => (j === i ? [x[0], !x[1]] : x)); persist({ todos: next }, () => setTodos(next)); };

    const isShare = (item.tags || []).includes("bi-share");
    const summarise = async () => {
      setSummarising(true);
      try { const s = onSummarise ? await onSummarise(item.id) : null; if (s) setSummary(s); }
      catch { onToast && onToast("Summarise failed"); }
      finally { setSummarising(false); }
    };
    const del = () => { if (delStep < 2) setDelStep(delStep + 1); else { onDelete && onDelete(item.id); onToast("Deleted"); } };

    // shared-BI-entity variant
    if (isShare) {
      const relBadge = (r) => { const m = { look: ["Lookalike", "look"], comp: ["Complementary", "comp"], alt: ["Alternative", "alt"] }[r]; return <span key={r} className={"bi-badge bi-badge--" + m[1]}>{m[0]}</span>; };
      return (
        <div className="inbox-detail">
          <button className="bi-btn bi-btn--sm inbox-back" onClick={onBack} style={{ marginBottom: 12, alignSelf: "flex-start" }}><I name="chevron-left" size={14} />Back</button>
          <div className="inbox-detail__head">
            <div className="inbox-detail__titlerow"><span className="inbox-detail__title" style={{ cursor: "default" }}>{item.title}</span></div>
            {/* The section stays on the detail whatever the switcher is doing:
                it is a fact about the row, not the list's Section column, and
                an approval writes the entity into THIS piece of the website. */}
            <div className="inbox-detail__meta">{statusPill(item.status)}<span className="inbox-item__src">{item.source} · {item.when}</span><SectionChip item={item} /><SharedChip item={item} /></div>
          </div>
          <div className="inbox-share-banner">
            <I name="share" size={18} />
            {/* The sender, named by the server from the property's own pieces
                where it can be, with the label carried in the share payload as
                the fallback: a share can arrive from another property, whose
                sections this website cannot name. */}
            <div>Shared {item.shareKind} from <b>{item.sharedFromName || item.shareSite}</b>. Review, then approve to add it to <b>{item.sectionName || "this site"}</b>'s <b>{item.shareModule}</b> module (or reject).</div>
          </div>
          <div className="inbox-section" style={{ borderTop: "none", paddingTop: 0 }}>
            <div className="inbox-section__h">Entity</div>
            {/* The entity's URL and relationships live in the share payload,
                which only the detail fetch carries. Approve does not need the
                fetch (the backend re-reads raw_content itself), so a failed
                fetch degrades this section without blocking the decision. */}
            {contentState === "loading" ? <div className="inbox-section__empty" style={{ display: "flex", alignItems: "center", gap: 8 }}><span className="ml-spinner ml-spinner--sm" />Loading the shared entity…</div>
              : contentState === "error" ? <div className="inbox-section__empty">Couldn't load the shared details. <button className="inbox-content-preview__toggle" onClick={onRetryContent}>Retry</button></div>
              : <>
                  {item.url ? <a className="inbox-src-link" href={outbound(item.url)} target="_blank" rel="noopener noreferrer"><I name="link" size={14} />{item.url}</a> : <span className="inbox-src-link">No URL</span>}
                  <div className="inbox-tags" style={{ marginTop: 10 }}>{(item.rels || []).map(relBadge)}</div>
                </>}
          </div>
          <div className="inbox-detail-actions" style={{ marginTop: 16 }}>
            {/* Approve used to toast "Added to X" and add nothing. POST
                /inbox/:id/approve-share creates the entity in this site's
                module and marks the item used, which is what the copy claims. */}
            <button className="bi-btn bi-btn--primary" disabled={sharing} onClick={async () => {
              setSharing(true);
              try { await onApproveShare(item.id); onToast("Added to " + item.shareModule); }
              catch (e) { onToast(e.message || "Could not add it"); }
              finally { setSharing(false); }
            }}><I name="check" size={16} />{sharing ? "Adding…" : `Approve & add to ${item.shareModule}`}</button>
            {/* Reject had no handler at all. Archiving is the reversible no. */}
            <button className="bi-btn" disabled={sharing} onClick={async () => {
              try { await onArchive(item.id, true); onToast("Rejected"); }
              catch (e) { onToast(e.message || "Could not reject it"); }
            }}><I name="x" size={16} />Reject</button>
          </div>
        </div>
      );
    }

    return (
      <div className="inbox-detail">
        <button className="bi-btn bi-btn--sm inbox-back" onClick={onBack} style={{ marginBottom: 12, alignSelf: "flex-start" }}><I name="chevron-left" size={14} />Back</button>
        <div className="inbox-detail__head">
          <div className="inbox-detail__titlerow">
            <span className={"inbox-priority-dot inbox-pri-" + item.pri} style={{ marginTop: 11 }} />
            {editing
              ? <input className="inbox-detail__title-input" value={title} autoFocus onChange={e => setTitle(e.target.value)} onBlur={commitTitle} onKeyDown={e => { if (e.key === "Enter") commitTitle(); if (e.key === "Escape") { setTitle(item.title); setEditing(false); } }} />
              : <span className="inbox-detail__title" onClick={() => setEditing(true)} title="Click to edit">{title}</span>}
          </div>
          <div className="inbox-detail__meta">{statusPill(archived ? "archived" : item.status)}<span className="inbox-item__src">{item.source} · {item.when}</span><SectionChip item={item} /><SharedChip item={item} /></div>
          <div className="inbox-detail-actions">
            <button className="bi-btn bi-btn--sm bi-btn--ai" onClick={() => onGenerate(item)}><I name="edit" size={14} />Generate article</button>
            <button className="bi-btn bi-btn--sm bi-btn--ai" disabled={summarising} onClick={summarise}><I name="sparkles" size={14} />Summarise</button>
            <button className="bi-btn bi-btn--sm" onClick={() => { const next = !archived; setArchived(next); onArchive && onArchive(item.id, next); onToast(next ? "Archived" : "Restored"); }}><I name="archive" size={14} />{archived ? "Unarchive" : "Archive"}</button>
            <button className={"bi-btn bi-btn--sm bi-btn--danger" + (delStep === 1 ? " inbox-btn-delete-confirm" : delStep === 2 ? " inbox-btn-delete-final" : "")} onClick={del} onMouseLeave={() => setDelStep(0)}>
              <I name="trash" size={14} />{delStep === 0 ? "Delete" : delStep === 1 ? "Are you sure?" : "Confirm delete"}
            </button>
          </div>
        </div>

        {item.url && <div className="inbox-section"><div className="inbox-section__h"><I name="link" size={12} />Source</div><a className="inbox-src-link" href={outbound(item.url)} target="_blank" rel="noopener noreferrer">{item.url}<I name="external-link" size={14} /></a></div>}

        <div className="inbox-section">
          <div className="inbox-section__h"><I name="sparkles" size={12} />AI summary</div>
          {summarising ? <div className="inbox-section__empty" style={{ display: "flex", alignItems: "center", gap: 8 }}><span className="ml-spinner ml-spinner--sm ml-spinner--ai" />Generating summary…</div>
            : summary ? <div className="inbox-summary-block">{summary}</div>
            : <div className="inbox-section__empty">No summary yet. Click Summarise.</div>}
        </div>

        <div className="inbox-section">
          <div className="inbox-section__h"><I name="file-text" size={12} />Content preview</div>
          {/* The list endpoint omits raw_content, so this section waits on the
              per-item detail fetch rather than rendering a blank forever. */}
          {contentState === "loading" ? <div className="inbox-section__empty" style={{ display: "flex", alignItems: "center", gap: 8 }}><span className="ml-spinner ml-spinner--sm" />Loading content…</div>
            : contentState === "error" ? <div className="inbox-section__empty">Couldn't load the content. <button className="inbox-content-preview__toggle" onClick={onRetryContent}>Retry</button></div>
            : item.content ? <>
                <div className="inbox-content-preview">{expanded ? item.content : item.content.slice(0, 240) + (item.content.length > 240 ? "…" : "")}</div>
                {item.content.length > 240 && <button className="inbox-content-preview__toggle" onClick={() => setExpanded(e => !e)}>{expanded ? "Collapse" : "View full content"}</button>}
              </>
            : <div className="inbox-section__empty">No content captured for this item.</div>}
        </div>

        <div className="inbox-section">
          <div className="inbox-section__h"><I name="tag" size={12} />Tags</div>
          <div className="inbox-tags">
            {tags.map(t => <span className="inbox-tag" key={t}>{t}<span className="inbox-tag__x" onClick={() => removeTag(t)}><I name="x" size={12} /></span></span>)}
            <input className="inbox-tag-add" placeholder="Add tag" value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && tagInput.trim()) { addTag(tagInput.trim()); setTagInput(""); } }} />
          </div>
        </div>

        <div className="inbox-section">
          <div className="inbox-section__h"><I name="check-square" size={12} />Checklist</div>
          {todos.map(([t, done], i) => (
            <label className="inbox-todo" key={i}>
              <span className="hds-check"><input type="checkbox" checked={done} onChange={() => toggleTodo(i)} /><span className="hds-check__box"><I name="check" size={12} /></span></span>
              <span className={"inbox-todo__text" + (done ? "" : "")}>{t}</span>
            </label>
          ))}
          <div className="inbox-todo">
            <input className="inbox-tag-add inbox-addtodo" placeholder="+ Add todo" value={todoInput} onChange={e => setTodoInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && todoInput.trim()) { addTodo(todoInput.trim()); setTodoInput(""); } }} />
          </div>
        </div>

        <div className="inbox-section">
          <div className="inbox-section__h"><I name="file-text" size={12} />Linked article</div>
          {item.linked ? <a className="inbox-linked-post" href="#" onClick={e => e.preventDefault()}><I name="file-text" size={15} />{item.linked}</a> : <div className="inbox-section__empty">None yet.</div>}
        </div>
      </div>
    );
  }

  function Inbox({ onGenerate, site, sectionId, sections }) {
    const siteId = site && (site.site_id != null ? site.site_id : site.id);
    // The All position is `sectionId == null`, and it is the whole website.
    const whole = sectionId == null;
    const sectionNode = whole ? null
      : ((sections || []).find(s => (s.site_id != null ? s.site_id : s.id) === sectionId) || null);
    // `site_id` authorises the request and `section_id` narrows it, so the two
    // are never interchangeable here. The section is OMITTED at All rather than
    // sent as a value meaning everything: an absent filter and a filter naming
    // every section are different requests, and only the first one is the
    // question "what does this website hold".
    const scoped = (path) => path + (whole ? "" : `&section_id=${sectionId}`);
    const [filter, setFilter] = useState("all");
    const [selId, setSelId] = useState(null);
    const [view, setView] = useState("list"); // mobile
    const [toast, setToast] = useState(null);
    const [items, setItems] = useState([]);
    const [listState, setListState] = useState("loading"); // loading | ready | error
    const [listErr, setListErr] = useState(null);
    const [adding, setAdding] = useState(false);

    // Both reads take the anchor and the same optional section, in one callback
    // so the list and the arithmetic behind its header can never be answered at
    // two different positions of the switcher.
    const [stats, setStats] = useState(null);
    const [statsErr, setStatsErr] = useState(null);
    const load = useCallback(async () => {
      if (siteId == null) { setItems([]); setStats(null); setListState("ready"); return; }
      setListState("loading"); setListErr(null); setStatsErr(null);
      // The breakdown is a separate request and a separate failure: an inbox
      // that loads must not be hidden because its arithmetic did not.
      api(scoped(`/inbox/stats?site_id=${siteId}`))
        .then(s => { setStats(s); setStatsErr(null); })
        .catch(e => { setStats(null); setStatsErr((e && e.message) || "Request failed"); });
      try {
        const rows = await api(scoped(`/inbox?site_id=${siteId}`));
        const mapped = (Array.isArray(rows) ? rows : []).map(mapInbox);
        setItems(mapped); setSelId(s => (mapped.some(i => i.id === s) ? s : (mapped[0] && mapped[0].id) || null));
        setListState("ready");
      } catch (e) { setItems([]); setListErr((e && e.message) || "Request failed"); setListState("error"); }
    }, [siteId, sectionId]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [load]);

    // The list endpoint drops raw_content on purpose (an item can hold a whole
    // article, or a BI share payload), so the full row comes from GET /inbox/:id
    // on selection. raw_content never changes after capture, so one fetch per id
    // is cached for the session; only a failed fetch is forgotten, so Retry (or
    // reselecting) can try again. detailTick exists purely to re-arm the effect
    // when Retry asks for the same, still-selected id.
    const [detail, setDetail] = useState({});   // id -> { state: loading|ready|error, content }
    const [detailTick, setDetailTick] = useState(0);
    const detailSeen = useRef(new Set());
    useEffect(() => {
      const id = selId;
      if (id == null || detailSeen.current.has(id)) return;
      detailSeen.current.add(id);
      setDetail(d => ({ ...d, [id]: { state: "loading", content: "" } }));
      api(`/inbox/${id}`)
        .then(row => setDetail(d => ({ ...d, [id]: { state: "ready", content: (row && row.raw_content) || "" } })))
        .catch(() => { detailSeen.current.delete(id); setDetail(d => ({ ...d, [id]: { state: "error", content: "" } })); });
    }, [selId, detailTick]);
    const retryDetail = useCallback(() => { setDetailTick(t => t + 1); }, []);

    // The filter counts come from the ROWS THIS SCREEN HOLDS, which is the
    // whole answer for the position and not a page of it: GET /inbox returns
    // every item the filter selects with no cap, so counting them here is
    // counting live state. GET /inbox/stats counts the same set in the
    // database, and the two are deliberately not mixed into one number: one
    // figure with two sources is how a screen comes to disagree with itself.
    // The breakdown is what stats is read for, and the tab counts stay
    // checkable against the list they filter.
    const counts = { all: items.length }; FILTERS.slice(1).forEach(([id]) => counts[id] = items.filter(i => i.status === id).length);
    const rows = filter === "all" ? items : items.filter(i => i.status === filter);
    const sel = items.find(i => i.id === selId);
    // What the pane shows: the light list row overlaid with the fetched
    // raw_content, and for a BI share, the labels parsed out of that payload.
    // Share items have no source_url of their own, the entity's URL rides in
    // the payload, so it fills item.url only when the row left it empty.
    const selDetail = sel ? detail[sel.id] : null;
    const selState = selDetail ? selDetail.state : "loading";
    let selItem = sel;
    if (sel) {
      const content = (selDetail && selDetail.content) || "";
      selItem = { ...sel, content };
      if ((sel.tags || []).includes("bi-share")) {
        const si = shareInfo(sel, content);
        selItem = { ...selItem, ...si, url: sel.url || si.shareUrl };
      }
    }
    const showToast = (m) => { setToast(m); setTimeout(() => setToast(null), 2200); };

    // EVERY PER ROW CALL BELOW, CHECKED AGAINST ONE ROW OF ANOTHER SECTION,
    // because at All this list holds rows from several of them and the
    // switcher's anchor is the right site for exactly one. None of these five
    // routes takes a site at all: GET /inbox/:id, PATCH, DELETE, summarize and
    // approve-share are addressed by the item's own id (routes/inbox.js), and
    // approve-share reads `item.site_id` off the row itself to decide which
    // piece of the website gains the entity. So there is nothing here to
    // repoint, and the thing worth recording is that it was looked at: an id
    // only route is safe at All and a route taking a site is not.
    const onSummarise = async (id) => { const it = await api(`/inbox/${id}/summarize`, { method: "POST", body: "{}" }); await load(); return it && it.ai_summary; };
    const onArchive = async (id, toArchived) => { try { await api(`/inbox/${id}`, { method: "PATCH", body: JSON.stringify({ status: toArchived ? "archived" : "new" }) }); await load(); } catch { /* */ } };
    const onDelete = async (id) => { try { await api(`/inbox/${id}`, { method: "DELETE" }); await load(); } catch { /* */ } };
    // Field edits (title, tags, checklist). Throws on failure so the detail
    // panel can say so rather than leaving the change looking saved.
    const onPatch = async (id, patch) => { await api(`/inbox/${id}`, { method: "PATCH", body: JSON.stringify(patch) }); await load(); };
    // A shared BI entity, accepted: creates it in this site's module and marks
    // the inbox item used. Was a toast with nothing behind it.
    const onApproveShare = async (id) => { await api(`/inbox/${id}/approve-share`, { method: "POST", body: "{}" }); await load(); };

    return (
      <div className="bi-page" style={{ maxWidth: "none", paddingBottom: 24 }}>
        <div className="bi-header" style={{ marginBottom: 16 }}>
          {/* The subtitle names the POSITION and no longer the anchor: the list
              is the whole website's at All, and naming one connection there
              would be the screen claiming a scope it stopped having. */}
          <div className="bi-header__titles"><h1 className="bi-title"><span className="bi-title__icon"><I name="inbox" size={20} /></span>Inbox</h1><div className="bi-subtitle">Capture &amp; triage · {whole ? "every piece of this website" : ((sectionNode && sectionNode.name) || (site && site.name) || "one section")}</div></div>
        </div>

        {/* At All, and only there, because at a section the breakdown is one
            row and that row is the header counts again. Nothing is hidden by
            its absence: a filtered position shows the section it names. */}
        {whole && <Breakdown stats={stats} err={statsErr} onRetry={load} />}

        <div className="inbox-layout" data-view={view}>
          <div className="inbox-left">
            <div className="inbox-filters">
              <StatusFilter value={filter} counts={counts} onPick={setFilter} />
            </div>
            <div className="inbox-list">
              {listState === "loading" ? <div className="inbox-skeleton">{[0,1,2,3].map(i => <div className="inbox-skel-row" key={i}><div className="inbox-skel-line" style={{ width: "80%" }} /><div className="inbox-skel-line" style={{ width: "50%" }} /></div>)}</div>
                /* A failed load used to fall through to the empty-state copy,
                   so an outage read as "inbox zero". Say what happened. */
                : listState === "error" ? (
                  <div className="inbox-empty" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                    <I name="alert-triangle" size={20} style={{ color: "var(--danger)" }} />
                    <div style={{ fontWeight: 600 }}>Couldn't load the inbox</div>
                    {listErr && <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{listErr}</div>}
                    <button className="bi-btn bi-btn--sm" onClick={load}>Retry</button>
                  </div>)
                : rows.length === 0 ? <div className="inbox-empty">No items in this filter.</div>
                : rows.map(it => (
                  <div key={it.id} className={"inbox-item" + (it.id === selId ? " inbox-item--selected" : "")} onClick={() => { setSelId(it.id); setView("detail"); }}>
                    <span className={"inbox-priority-dot inbox-pri-" + (it.pri || "low")} />
                    <div className="inbox-item__body">
                      <div className="inbox-item__title">{(it.tags || []).includes("bi-share") && <I name="share" size={12} style={{ verticalAlign: "-1px", marginRight: 4, color: "var(--primary)" }} />}{it.title}</div>
                      {/* THE SECTION COLUMN, which this layout wears as a row
                          label because the list is cards and not a table. It is
                          removed when the switcher names a section and nothing
                          else changes with it (MODULE-SCOPE 1.1). Provenance is
                          not part of it and stays: who sent an item and whose
                          inbox holds it are different facts, and only the
                          second one is what the filter just answered. */}
                      <div className="inbox-item__meta">{statusPill(it.status)}<span className="inbox-item__src">{it.source} · {it.when}</span>{whole && <SectionChip item={it} />}<SharedChip item={it} /></div>
                    </div>
                  </div>
                ))}
            </div>
            <div className="inbox-add-foot"><button className="bi-btn bi-btn--primary hds-btn--block" style={{ width: "100%", justifyContent: "center" }} onClick={() => setAdding(true)}><I name="plus" size={16} />Add content</button></div>
          </div>
          <div className="inbox-right">
            {sel ? <Detail item={selItem} contentState={selState} onRetryContent={retryDetail} onToast={showToast} onGenerate={onGenerate} onBack={() => setView("list")} onSummarise={onSummarise} onArchive={onArchive} onDelete={onDelete} onPatch={onPatch} onApproveShare={onApproveShare} />
              : <div className="inbox-detail-placeholder"><I name="inbox" size={40} /><div>Select an item to view details</div></div>}
          </div>
        </div>

        {adding && <AddContent siteId={siteId} siteLabel={site && site.name} whole={whole} onClose={() => setAdding(false)}
                                onAdded={async () => { setAdding(false); showToast("Added to inbox"); await load(); }} />}

        {toast && <div className="inbox-toast"><I name="check-circle" size={16} />{toast}</div>}
      </div>
    );
  }

  window.MlPipelineInbox = { Inbox };
})();
